import { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import axios from "axios";
import { Plus, X, Trash2 } from "lucide-react";
import DataTable from "../ui/DataTable";
import GroupedRowActionSheet, { ICON_PROPS } from "../ui/GroupedRowActionSheet";
import DashboardNavbar from "../dashboardNavbar";
import {
  Act,
  Btn,
  SectionHead,
  Scrim,
  Modal,
  Field,
  Input,
} from "../ui/primitives";
import { LabelWithHelp } from "../ui/FieldHelp";
import { SETTINGS_FIELD_HELP as HELP } from "../ui/settingsFieldHelp";
import { useConfirm } from "../ui/ConfirmProvider";
import { SYSTEM_TABS } from "../admin/adminSectionTabs";
import {
  IMPORT_COURSE_LIST_DEFAULTS,
  IMPORT_COURSE_SORT_FIELDS,
  getNextImportCourseSort,
  sortImportCoursesLocally,
} from "./importCourseListApi";
import useDemoActionGate from "../hooks/useDemoActionGate";
import DemoLockedDialog from "../ui/DemoLockedDialog";

/* ---- on-brand layout shell (matches the other System section pages) ---- */
const Shell = styled.div`
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  @media (max-width: 768px) {
    padding: 0 14px 28px;
  }
`;

const Inner = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.gap};
`;

/* result list inside the "Add" modal */
const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 320px;
  overflow-y: auto;
  margin-top: 4px;

  .result-row {
    display: flex;
    flex-direction: column;
    gap: 2px;
    text-align: left;
    width: 100%;
    padding: 10px 12px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: ${({ theme }) => theme.rMd};
    background: ${({ theme }) => theme.surface2};
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
  }
  .result-row:hover {
    background: ${({ theme }) => theme.surface};
    border-color: ${({ theme }) => theme.accentLine};
  }
  .result-primary {
    font-weight: 650;
    font-size: 13.5px;
    color: ${({ theme }) => theme.text};
  }
  .result-sub {
    font-size: ${({ theme }) => theme.fsMicro};
    color: ${({ theme }) => theme.textSubtle};
  }
  .result-empty {
    padding: 18px 4px;
    text-align: center;
    color: ${({ theme }) => theme.textMuted};
    font-size: ${({ theme }) => theme.fsSm};
  }
`;

const ImportCourseGrid = () => {
  const askConfirmation = useConfirm();
  const { guardAction, lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [importSortBy, setImportSortBy] = useState(IMPORT_COURSE_LIST_DEFAULTS.sortBy);
  const [importSortDir, setImportSortDir] = useState(IMPORT_COURSE_LIST_DEFAULTS.sortDir);

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  useEffect(() => {
    fetchImportCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchImportCourses = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/api/import-course`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: getBasicAuthHeader(),
        },
      });
      setRowData(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching import courses:", error);
      setRowData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!guardAction('action.system.importCatalog.remove')) return;
    const confirmed = await askConfirmation({
      title: 'Delete record?',
      message: 'Delete this record?',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;
    await axios.delete(`${apiUrl}/api/import-course/${row.userId}`, {
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        Authorization: getBasicAuthHeader(),
      },
    });
    fetchImportCourses();
  };

  // 🔍 SEARCH USERS API
  const handleSearch = async (value) => {
    setSearchTerm(value);
    if (!value) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const callerId = localStorage.getItem('userId1') || localStorage.getItem('userId');
      const response = await axios.get(`${apiUrl}/api/users?search=${value}&userId=${callerId}`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
          Authorization: getBasicAuthHeader(),
        },
      });
      setSearchResults(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setSearching(false);
    }
  };

  // ➕ POST IMPORT COURSE
  const handleSelectEmail = async (user) => {
    if (!guardAction('action.system.importCatalog.add')) return;
    try {
      await axios.post(`${apiUrl}/api/import-course`, user.userId, {
        headers: {
          "Content-Type": "text/plain",
          Authorization: getBasicAuthHeader(),
        },
        transformRequest: [(data) => data],
      });

      closeAddDialog();
      fetchImportCourses();
    } catch (error) {
      console.error("Failed to add import course", error);
    }
  };

  const closeAddDialog = () => {
    setOpen(false);
    setSearchTerm("");
    setSearchResults([]);
  };

  const sortedRowData = useMemo(
    () => sortImportCoursesLocally(rowData || [], importSortBy, importSortDir),
    [rowData, importSortBy, importSortDir],
  );

  const handleImportSortChange = (columnKey) => {
    const next = getNextImportCourseSort(columnKey, importSortBy, importSortDir);
    setImportSortBy(next.sortBy);
    setImportSortDir(next.sortDir);
  };

  return (
    <>
      <Shell>
        <DashboardNavbar menuItems={SYSTEM_TABS} />
        <Inner>
          <SectionHead>
            <div>
              <h2>Course Catalog</h2>
              <p>Imported course sources reused across your organization</p>
            </div>
            <Btn $variant="primary" onClick={() => {
              if (!guardAction('action.system.importCatalog.add')) return;
              setOpen(true);
            }}>
              <Plus size={15} />
              Add
            </Btn>
          </SectionHead>

          <DataTable
            rows={sortedRowData}
            loading={loading}
            rowKey={(r, i) => r.userId ?? r.id ?? i}
            sortBy={importSortBy}
            sortDir={importSortDir}
            onSortChange={handleImportSortChange}
            search
            searchPlaceholder="Search name, email…"
            searchKeys={["name", "email"]}
            primary={{
              header: "Name",
              sortKey: IMPORT_COURSE_SORT_FIELDS.name,
              label: (r) => r.name || r.email,
              sub: (r) => r.email,
            }}
            columns={[{ header: "Email", sortKey: IMPORT_COURSE_SORT_FIELDS.email, render: (r) => r.email, muted: true }]}
            actionsColumnWidth={52}
            actions={(r) => (
              <GroupedRowActionSheet
                entityLabel="Course source"
                title={r.name || r.email}
                menuAriaLabel="Course source actions"
                dangerItem={{
                  key: 'delete',
                  icon: <Trash2 {...ICON_PROPS} />,
                  label: 'Remove from catalog',
                  ...lockProps('action.system.importCatalog.remove'),
                }}
                onAction={() => handleDelete(r)}
              />
            )}
            emptyText="No imported courses yet."
          />
        </Inner>

        {/* ➕ Add course source */}
        {open && (
          <Scrim onClick={closeAddDialog}>
            <Modal
              style={{ maxWidth: 520 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal__head">
                <div>
                  <h3>Add to catalog</h3>
                  <p>Search a user by email to import their courses.</p>
                </div>
                <Act className="x-btn" title="Close" onClick={closeAddDialog}>
                  <X size={18} />
                </Act>
              </div>
              <div className="modal__body">
                <Field style={{ marginBottom: 8 }}>
                  <label><LabelWithHelp help={HELP.catalogSearchEmail}>Search user by email</LabelWithHelp></label>
                  <Input
                    autoFocus
                    value={searchTerm}
                    placeholder="name@example.com"
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </Field>
                <ResultList>
                  {searching && <div className="result-empty">Searching…</div>}
                  {!searching &&
                    searchResults.map((user) => (
                      <button
                        type="button"
                        key={user.userId}
                        className="result-row"
                        onClick={() => handleSelectEmail(user)}
                      >
                        <span className="result-primary">{user.email}</span>
                        {user.name && (
                          <span className="result-sub">{user.name}</span>
                        )}
                      </button>
                    ))}
                  {!searching && searchTerm && searchResults.length === 0 && (
                    <div className="result-empty">No matching users found.</div>
                  )}
                </ResultList>
              </div>
            </Modal>
          </Scrim>
        )}
      </Shell>
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
};

export default ImportCourseGrid;
